(() => {
  'use strict';
  const KEY = '__DivettaFaqPage';
  window[KEY]?.destroy?.();
  const globalController = new AbortController();
  const instances = new Map();
  const SELECTOR = '[data-ld-faq-page]';

  const normalize = (value) => String(value || '')
    .toLocaleLowerCase(document.documentElement.lang || 'it')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  function init(root) {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const state = { controller, observer: null };
    instances.set(root, state);
    root.dataset.ldFaqReady = 'true';

    const search = root.querySelector('[data-ld-faq-search]');
    const clear = root.querySelector('[data-ld-faq-clear]');
    const reset = root.querySelector('[data-ld-faq-reset]');
    const count = root.querySelector('[data-ld-faq-count]');
    const empty = root.querySelector('[data-ld-faq-empty]');
    const filters = Array.from(root.querySelectorAll('[data-ld-faq-filter]'));
    const items = Array.from(root.querySelectorAll('[data-ld-faq-item]'));
    let activeCategory = 'all';

    const render = () => {
      const query = normalize(search?.value);
      let visible = 0;

      items.forEach((item) => {
        const categoryMatch = activeCategory === 'all' || item.dataset.category === activeCategory;
        const searchMatch = !query || normalize(item.dataset.searchText).includes(query);
        const show = categoryMatch && searchMatch;
        item.hidden = !show;
        if (show) visible += 1;
      });

      if (count) {
        count.textContent = visible === 1 ? '1 risposta disponibile' : `${visible} risposte disponibili`;
      }
      if (empty) empty.hidden = visible !== 0;
      if (clear) clear.hidden = !query;
    };

    const setCategory = (category) => {
      activeCategory = category;
      filters.forEach((button) => {
        const active = button.dataset.ldFaqFilter === category;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      render();
    };

    filters.forEach((button) => {
      button.addEventListener('click', () => setCategory(button.dataset.ldFaqFilter || 'all'), { signal });
    });

    search?.addEventListener('input', render, { signal });
    clear?.addEventListener('click', () => {
      if (!search) return;
      search.value = '';
      search.focus();
      render();
    }, { signal });

    reset?.addEventListener('click', () => {
      if (search) search.value = '';
      setCategory('all');
      search?.focus();
    }, { signal });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reveals = Array.from(root.querySelectorAll('[data-ld-faq-reveal]'));
    const syncMotion = () => {
      state.observer?.disconnect();
      state.observer = null;
      const animate = !reduceMotion.matches && 'IntersectionObserver' in window && reveals.length > 0;
      root.classList.toggle('is-motion-enabled', animate);
      if (animate) {
        state.observer = new IntersectionObserver((entries) => {
          if (signal.aborted) return;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            state.observer?.unobserve(entry.target);
          });
        }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
        reveals.filter((element) => !element.classList.contains('is-visible'))
          .forEach((element) => state.observer.observe(element));
      } else {
        reveals.forEach((element) => element.classList.add('is-visible'));
      }
    };
    reduceMotion.addEventListener('change', syncMotion, { signal });
    syncMotion();

    render();
  }

  const destroyRoot = (root) => {
    const state = instances.get(root);
    if (!state) return;
    state.controller.abort();
    state.observer?.disconnect();
    root.classList.remove('is-motion-enabled');
    root.removeAttribute('data-ld-faq-ready');
    instances.delete(root);
  };
  const initAll = (scope = document) => {
    Array.from(instances.keys()).filter((root) => !root.isConnected).forEach(destroyRoot);
    if (scope.matches?.(SELECTOR)) init(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(init);
  };
  const { signal } = globalController;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true, signal });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target), { signal });
  document.addEventListener('shopify:section:unload', (event) => {
    Array.from(instances.keys()).forEach((root) => {
      if (event.target === root || event.target.contains?.(root)) destroyRoot(root);
    });
  }, { signal });
  window[KEY] = {
    destroy() {
      globalController.abort();
      Array.from(instances.keys()).forEach(destroyRoot);
      delete window[KEY];
    }
  };
})();
